import {
  Button,
  Calendar,
  CalendarMdIcon,
  Checkbox,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  TimePicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@crono-one/design-system';
import { format, isBefore, parseISO, startOfDay } from 'date-fns';
import {
  toUtcCalendarDay,
  toUtcEndOfLocalDay,
  toUtcMidnightOfLocalDay,
} from 'utils/dateUtils';
import { useFormik } from 'formik';
import usePatchMeeting from 'hooks/services/calendar/usePatchMeeting';
import { MeetingProvider } from 'hooks/services/calendar/useDeleteMeeting';
import { toast } from '@crono-one/design-system/side-effects';

const getNow = () => new Date();

const applyDate = (base: Date, dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) return new Date(base);
  const updated = new Date(base);
  updated.setFullYear(year, month - 1, day);
  return updated;
};

const applyTime = (base: Date, timeString: string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const updated = new Date(base);
  updated.setHours(hours, minutes, 0, 0);
  return updated;
};

// All-day meetings are floating calendar dates stored as UTC midnight, so they
// are read back as their UTC day to mirror of the normalization applied on
// submit, without which a negative-offset viewer would be shown (and would
// resubmit) the previous day.
const resolveInitialDate = (
  isoDate: string | null | undefined,
  isAllDay: boolean,
): Date => {
  if (!isoDate) {
    return getNow();
  }

  const parsed = parseISO(isoDate);
  return isAllDay ? toUtcCalendarDay(parsed) : parsed;
};

interface FormValues {
  startDate: Date;
  endDate: Date;
  isAllDay: boolean;
}

interface RescheduleMeetingProps {
  children?: JSX.Element;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: number;
  provider: MeetingProvider;
  initialStartDate?: string | null;
  initialEndDate?: string | null;
  initialIsAllDay?: boolean;
}

export const RescheduleMeeting = (props: RescheduleMeetingProps) => {
  const {
    children,
    open,
    onOpenChange,
    meetingId,
    provider,
    initialStartDate,
    initialEndDate,
    initialIsAllDay,
  } = props;

  const { mutate: patchMeeting, isPending } = usePatchMeeting();

  const formik = useFormik<FormValues>({
    initialValues: {
      startDate: resolveInitialDate(initialStartDate, !!initialIsAllDay),
      endDate: resolveInitialDate(initialEndDate, !!initialIsAllDay),
      isAllDay: initialIsAllDay ?? false,
    },
    validate: ({ startDate, endDate, isAllDay }) => {
      if (!isAllDay && !isBefore(startDate, endDate)) {
        return { endDate: 'End must be after start' };
      }
      return {};
    },
    onSubmit: ({ startDate, endDate, isAllDay }) => {
      // All-day meetings are floating calendar dates stored as UTC midnight, so the
      // picked days are normalized here rather than when the checkbox is toggled —
      // that way changing the date afterwards can't reintroduce the local offset.
      const start = isAllDay ? toUtcMidnightOfLocalDay(startDate) : startDate;
      const end = isAllDay ? toUtcEndOfLocalDay(endDate) : endDate;

      patchMeeting(
        {
          meetingId,
          provider,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          isAllDay,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            toast.success('Meeting rescheduled successfully');
          },
        },
      );
    },
  });

  const endInvalid = !!formik.errors.endDate;

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          formik.resetForm();
        }
        onOpenChange(open);
      }}
    >
      {children && <DialogTrigger render={children} />}

      <DialogContent className="h-150 w-162.5">
        <form className="h-full flex flex-col" onSubmit={formik.handleSubmit}>
          <DialogHeader className="mb-8">
            <DialogTitle>Select date and time</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-6 items-start">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-800" htmlFor="startDate">
                    From
                  </label>
                  <InputGroup className="min-w-36">
                    <InputGroupInput
                      id="startDate"
                      type="date"
                      value={format(formik.values.startDate, 'yyyy-MM-dd')}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'startDate',
                          applyDate(formik.values.startDate, e.target.value),
                        )
                      }
                      className="[&::-webkit-calendar-picker-indicator]:hidden"
                    />

                    <InputGroupAddon>
                      <CalendarMdIcon />
                    </InputGroupAddon>
                  </InputGroup>
                </div>

                {!formik.values.isAllDay && (
                  <div className="self-end">
                    <TimePicker
                      value={format(formik.values.startDate, 'HH:mm')}
                      onValueChange={(time) => {
                        if (!time) return;
                        formik.setFieldValue(
                          'startDate',
                          applyTime(formik.values.startDate, time),
                        );
                      }}
                      showTrigger={false}
                      className="min-w-24 max-w-24 w-24"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-800" htmlFor="endDate">
                      To
                    </label>
                    <InputGroup className="min-w-36">
                      <InputGroupInput
                        id="endDate"
                        type="date"
                        value={format(formik.values.endDate, 'yyyy-MM-dd')}
                        onChange={(e) =>
                          formik.setFieldValue(
                            'endDate',
                            applyDate(formik.values.endDate, e.target.value),
                          )
                        }
                        onBlur={() => formik.setFieldTouched('endDate', true)}
                        className="[&::-webkit-calendar-picker-indicator]:hidden"
                        aria-invalid={endInvalid}
                      />

                      <InputGroupAddon>
                        <CalendarMdIcon />
                      </InputGroupAddon>
                    </InputGroup>
                  </div>

                  {!formik.values.isAllDay && (
                    <div className="self-end">
                      <TimePicker
                        value={format(formik.values.endDate, 'HH:mm')}
                        onValueChange={(time) => {
                          if (!time) return;
                          formik.setFieldValue(
                            'endDate',
                            applyTime(formik.values.endDate, time),
                          );
                        }}
                        onBlur={() => formik.setFieldTouched('endDate', true)}
                        aria-invalid={endInvalid}
                        showTrigger={false}
                        className="min-w-24 max-w-24 w-24"
                      />
                    </div>
                  )}
                </div>
                {endInvalid ? (
                  <span className="text-sm text-red-500">
                    Meeting must end after it starts
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="isAllDay"
                  checked={formik.values.isAllDay}
                  onCheckedChange={(checked) => {
                    formik.setFieldValue('isAllDay', checked);
                  }}
                />

                <label htmlFor="isAllDay" className="text-sm text-gray-800">
                  All day
                </label>
              </div>
            </div>

            <div>
              <Calendar
                mode="range"
                selected={{
                  from: startOfDay(formik.values.startDate),
                  to: startOfDay(formik.values.endDate),
                }}
                defaultMonth={formik.values.startDate}
                onSelect={(range) => {
                  if (range?.from) {
                    const updated = new Date(formik.values.startDate);
                    updated.setFullYear(
                      range.from.getFullYear(),
                      range.from.getMonth(),
                      range.from.getDate(),
                    );
                    formik.setFieldValue('startDate', updated);
                  }
                  if (range?.to) {
                    const updated = new Date(formik.values.endDate);
                    updated.setFullYear(
                      range.to.getFullYear(),
                      range.to.getMonth(),
                      range.to.getDate(),
                    );
                    formik.setFieldValue('endDate', updated);
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter className="mt-auto">
            <DialogClose
              render={
                <Button
                  variant="outline"
                  size="lg"
                  disabled={isPending}
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
              }
            />

            <Button
              type="submit"
              size="lg"
              loading={isPending}
              disabled={!formik.isValid}
            >
              Reschedule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
